import React, { useState, useEffect, useLayoutEffect, useRef, useCallback } from "react";
import { Dialog } from "@headlessui/react";
import { Search, X, ChevronsLeft, ChevronLeft, ChevronRight, ChevronsRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DocumentMaterial } from "../types/Types";
import { useDocumentsMaterialSearch } from "../hooks/useDocumentsMaterialSearch";
import { useDocumentTypesContext } from "../context/DocumentTypesContext";

interface Props {
  disabled?: boolean;
  disabledTitle?: string;
  variant?: 'default' | 'compact';
  search: string;
  setSearch: (val: string) => void;
  searchField?: "code" | "name";
  setSearchField?: (val: "code" | "name") => void;
  docs: DocumentMaterial[];
  loading: boolean;
  page: number;
  totalPages: number;
  total: number;
  absoluteTotal: number;
  setPage: (p: number) => void;
  onSelect?: (doc: DocumentMaterial) => void;
  documentCode?: string; // To determine if we're in code 1700
  documentTypeId?: string; // Document type ID to filter navigation by document type
  currentDocumentNumber?: string; // Current document number to display in input
  forceListNavigation?: boolean; // Force list-based navigation (skip API navigate calls)
  inputWidth?: string; // Custom width for the search input (default: '15ch')
  showPlanName?: boolean; // Show plan name column instead of partner (for planning documents)
  hidePartnerColumn?: boolean; // Hide partner column entirely (for transfer documents)
  showItemColumn?: boolean; // Show item name column instead of partner (for work orders)
  // Nav-arrow overrides: when provided, the << < > >> buttons call these instead of the
  // built-in document navigation (e.g. driving a month selector instead of the docs list),
  // and use the matching *Disabled flag instead of the internal boundary checks.
  onFirst?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  onLast?: () => void;
  firstDisabled?: boolean;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  lastDisabled?: boolean;
  triggerLabel?: string;
  sourceTypeOptions?: Array<{ _id: string; code?: string; name?: string }>;
  sourceTypeId?: string;
  onSourceTypeChange?: (sourceTypeId: string) => void;
  // Controlled modal: the parent opens/closes it and no trigger/navigation is rendered.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  hideStatusColumn?: boolean;
  showLineCount?: boolean; // Extra "Items" column from doc.lineCount
  getDocNote?: (doc: DocumentMaterial) => string | null; // Non-empty note greys the row and shows the note
}

const DocumentsMaterialSearch: React.FC<Props> = ({
  disabled = false,
  disabledTitle,
  variant = 'default',
  search,
  setSearch,
  searchField = "code",
  setSearchField,
  docs,
  loading,
  page,
  totalPages,
  total,
  absoluteTotal,
  setPage,
  onSelect,
  documentCode,
  documentTypeId,
  currentDocumentNumber,
  forceListNavigation,
  inputWidth = '15ch',
  showPlanName,
  hidePartnerColumn,
  showItemColumn,
  onFirst,
  onPrev,
  onNext,
  onLast,
  firstDisabled,
  prevDisabled,
  nextDisabled,
  lastDisabled,
  triggerLabel,
  sourceTypeOptions,
  sourceTypeId,
  onSourceTypeChange,
  open,
  onOpenChange,
  hideTrigger,
  hideStatusColumn,
  showLineCount,
  getDocNote,
}) => {
  const { t } = useTranslation();
  const { documentTypes } = useDocumentTypesContext();
  const [internalOpen, setInternalOpen] = useState(false);
  const isModalOpen = open ?? internalOpen;
  const setIsModalOpen = (value: boolean) => {
    setInternalOpen(value);
    onOpenChange?.(value);
  };
  const [mainInput, setMainInput] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const [editedDocNumber, setEditedDocNumber] = useState<string | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const mainInputRef = useRef<HTMLInputElement>(null);

  const isPocetnaSostojba = documentTypes.find((documentType) => documentType.code === documentCode)?.pattern === "POCETNA-SOSTOJBA";
  const useListNavigation = forceListNavigation || documentCode === "Header"; // Use list-based navigation for buyer headers and planning documents
  const {
    isAtFirstDocument,
    isAtLastDocument,
    handleFirstDocument,
    handlePreviousDocument,
    handleNextDocument,
    handleLastDocument,
  } = useDocumentsMaterialSearch({
    docs,
    page,
    totalPages,
    setPage,
    onSelect,
    documentCode,
    documentTypeId,
    currentDocumentNumber,
    useListNavigation,
  });

  /** Prevents manual onScroll + IntersectionObserver + auto-fill from spamming duplicate page requests */
  const loadMoreLock = useRef(false);

  // Type-to-jump: lets the user type a known document number/name and press Enter to select it directly
  const [typed, setTyped] = useState<string | null>(null);
  const pendingCode = useRef<string | null>(null);
  const searchFieldRef = useRef(searchField);
  useEffect(() => {
    searchFieldRef.current = searchField;
  }, [searchField]);

  // "Name" column shown for a doc row - mirrors whichever column the table currently renders
  // (partner/supplier/buyer name, plan name, or item ident) depending on the active display mode
  const getDocDisplayName = useCallback((doc: DocumentMaterial): string | undefined => {
    if (showPlanName) return (doc as any).planName;
    if (showItemColumn) return (doc as any).item?.ident;
    return (doc.supplier as any)?.name ?? (doc.partner as any)?.name ?? (doc as any).primaryBuyer?.name ?? (doc as any).buyer?.name;
  }, [showPlanName, showItemColumn]);

  const getDocFieldValue = useCallback((doc: DocumentMaterial, field: "code" | "name"): string | undefined =>
    field === "code" ? doc.number : getDocDisplayName(doc), [getDocDisplayName]);

  const handleScrollLoadMore = (e: React.UIEvent<HTMLDivElement>) => {
    if (loading || page >= totalPages || loadMoreLock.current) return;
    const el = e.currentTarget;
    const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 120;
    if (nearBottom) {
      loadMoreLock.current = true;
      setPage(page + 1);
      window.requestAnimationFrame(() => {
        loadMoreLock.current = false;
      });
    }
  };

  // Reset editedDocNumber when currentDocumentNumber changes
  useEffect(() => {
    setEditedDocNumber(null);
  }, [currentDocumentNumber]);

  // Handle highlighted index when docs change
  useEffect(() => {
    if (docs.length === 0) {
      setHighlightedIndex(-1);
    } else {
      setHighlightedIndex(prev => {
        if (prev === -1 || page === 1) return 0;
        return prev < docs.length ? prev : docs.length - 1;
      });
    }
  }, [docs, page]);

  // Reset lock when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      loadMoreLock.current = false;
    }
  }, [isModalOpen]);

  // When modal opens: focus input, scroll to active doc (shown in blue, not yellow).
  // Only once per opening — re-running on every docs change (load more on scroll) jumped the list back up.
  const scrollToActivePending = useRef(false);
  useEffect(() => {
    if (isModalOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 150);
      setHighlightedIndex(-1);
      scrollToActivePending.current = true;
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (!isModalOpen || !scrollToActivePending.current || docs.length === 0) return;
    scrollToActivePending.current = false;
    if (!currentDocumentNumber) return;
    requestAnimationFrame(() => {
      const activeIdx = docs.findIndex((d) => d.number === currentDocumentNumber);
      if (activeIdx >= 0) {
        setTimeout(() => {
          const el = document.querySelector(`[data-row-index="${activeIdx}"]`);
          el?.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
      }
    });
  }, [isModalOpen, currentDocumentNumber, docs]);

  /**
   * Auto-fill: when the table is shorter than the scroll viewport there is no scrollbar,
   * so onScroll never fires. Keep fetching until the list fills the viewport or we hit the last page.
   */
  useLayoutEffect(() => {
    if (!isModalOpen || loading || page >= totalPages || loadMoreLock.current) return;
    const el = scrollContainerRef.current;
    if (!el || docs.length === 0) return;
    const slack = 8;
    if (el.scrollHeight <= el.clientHeight + slack) {
      loadMoreLock.current = true;
      setPage(page + 1);
      window.requestAnimationFrame(() => {
        loadMoreLock.current = false;
      });
    }
  }, [isModalOpen, loading, page, totalPages, docs.length, setPage]);

  // Infinite scroll observer — only active when modal is open and scroll container is mounted.
  // root must be the scroll container (not null/window) to avoid always-visible sentinel.
  useEffect(() => {
    if (!isModalOpen || !loadingRef.current || loading || page >= totalPages) return;

    const root = scrollContainerRef.current;
    const target = loadingRef.current;
    if (!root || !target) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !loadMoreLock.current) {
          loadMoreLock.current = true;
          setPage(page + 1);
          window.requestAnimationFrame(() => {
            loadMoreLock.current = false;
          });
        }
      },
      { threshold: 0, rootMargin: '80px', root }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [isModalOpen, loading, page, totalPages, setPage, docs.length]);

  // Resolve a typed document number/name once the parent's list (re-)fetches for it
  useEffect(() => {
    const code = pendingCode.current;
    if (!code || loading) return;
    pendingCode.current = null;
    const field = searchFieldRef.current;
    const matches = docs.filter(
      (d) => getDocFieldValue(d, field)?.toLowerCase() === code.toLowerCase()
    );
    if (matches.length === 1) {
      onSelect?.(matches[0]);
      setTyped(null);
      setSearch("");
      return;
    }
    // 0 matches or ambiguous -> escalate to the modal, already filtered by what they typed
    setIsModalOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [docs, loading]);

  const handleMainInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "F4") {
      e.preventDefault();
      if (typed && typed.trim()) setSearch(typed.trim());
      setIsModalOpen(true);
      setTimeout(() => searchInputRef.current?.focus(), 100);
      return;
    }
    if (e.key === "Escape") {
      setTyped(null);
      e.currentTarget.blur();
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
      const code = typed?.trim();
      if (!code) return;
      const field = searchFieldRef.current;
      const matches = docs.filter(
        (d) => getDocFieldValue(d, field)?.toLowerCase() === code.toLowerCase()
      );
      if (matches.length === 1) {
        onSelect?.(matches[0]);
        setTyped(null);
        setSearch("");
        return;
      }
      pendingCode.current = code;
      setSearch(code);
    }
  };

  const openModalAndSync = (value: string) => {
    setSearch(value);
    setIsModalOpen(true);
    // Focus search input after modal opens
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleMainInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setMainInput(val);

    if (val.trim().length > 0) {
      openModalAndSync(val);
      setMainInput(""); // optional: clear input after search triggered
    }
  };

  const handleSearch = () => {
    setSearch(search); // triggers search externally
  };

  const compact = variant === 'compact';
  const compactNavClass = `flex h-full w-7 shrink-0 items-center justify-center text-gray-600
    transition-colors hover:bg-gray-50 focus:outline-none focus:bg-blue-50 focus:ring-2
    focus:ring-inset focus:ring-blue-500/50 disabled:cursor-not-allowed disabled:bg-gray-50
    disabled:text-gray-300`;
  const hasNoDocuments = !currentDocumentNumber;
  const searchPlaceholder = searchField === "code"
    ? t("forms.searchByCode", { defaultValue: "Search by code" })
    : t("forms.searchByName", { defaultValue: "Search by name" });

  const handleKeyDownOnInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev < docs.length - 1 ? prev + 1 : prev;
        // Scroll to highlighted item with slight delay
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((prev) => {
        const newIndex = prev > 0 ? prev - 1 : prev;
        // Scroll to highlighted item with slight delay
        setTimeout(() => {
          const element = document.querySelector(`[data-row-index="${newIndex}"]`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
          }
        }, 50);
        return newIndex;
      });
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlightedIndex >= 0 && docs[highlightedIndex]) {
        onSelect?.(docs[highlightedIndex]);
        setIsModalOpen(false);
        setSearch(""); // Clear search when document is selected
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setIsModalOpen(false);
      setSearch(""); // Clear search when Escape is pressed
    }
  };

  return (
    <div className={compact
      ? 'relative inline-flex h-8 items-center rounded-md border border-gray-300 bg-white shadow-sm'
      : 'relative inline-flex items-center gap-0'}>
      {hideTrigger ? null : triggerLabel ? (
        <button
          type="button"
          disabled={disabled}
          title={disabled ? disabledTitle : undefined}
          onClick={() => setIsModalOpen(true)}
          className="px-2 h-7 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md transition-colors hover:bg-gray-50"
        >
          {triggerLabel}
        </button>
      ) : (
        <>
          <button
            className={compact ? compactNavClass : "p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-l-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"}
            onClick={onFirst ?? handleFirstDocument}
            disabled={disabled || (onFirst ? !!firstDisabled : (hasNoDocuments || isAtFirstDocument))}
            aria-label="First document"
            title={disabled ? disabledTitle : 'First document'}
          >
            <ChevronsLeft className={compact ? "h-3.5 w-3.5" : "w-4 h-4"} />
          </button>
          <button
            className={compact ? compactNavClass : "p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"}
            onClick={onPrev ?? handlePreviousDocument}
            disabled={disabled || (onPrev ? !!prevDisabled : (hasNoDocuments || isAtFirstDocument))}
            aria-label="Previous document"
            title={disabled ? disabledTitle : 'Previous document'}
          >
            <ChevronLeft className={compact ? "h-3.5 w-3.5" : "w-4 h-4"} />
          </button>
          <div className={compact ? "group relative h-full border-x border-gray-300" : "group relative"} style={{ width: inputWidth }}>
            <input
              ref={mainInputRef}
              disabled={disabled}
              title={disabled ? disabledTitle : undefined}
              type="text"
              placeholder={t("search.searchDocumentsAdvanced")}
              className={compact
                ? 'h-full w-full border-0 bg-white pl-1 pr-7 text-center font-mono text-xs tabular-nums text-gray-700 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/50 disabled:bg-gray-50 disabled:text-gray-400'
                : 'w-full pl-3 pr-9 py-2 h-9 border border-gray-300 bg-white shadow-sm focus:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-center font-semibold text-sm text-gray-800 cursor-text select-all'}
              value={typed ?? (currentDocumentNumber ?? "")}
              onChange={(e) => setTyped(e.target.value)}
              onFocus={(e) => e.target.select()}
              onBlur={() => {
                if (typed !== null && pendingCode.current === null) setTyped(null);
              }}
              onKeyDown={handleMainInputKeyDown}
            />
            <button
              type="button"
              disabled={disabled}
              title={disabled ? disabledTitle : undefined}
              onClick={() => setIsModalOpen(true)}
              className={compact
                ? 'absolute inset-y-0 right-0 flex w-7 items-center justify-center text-gray-500 hover:bg-gray-50 focus:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500/50'
                : 'absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 group-focus-within:text-blue-600 hover:text-gray-600 transition-colors flex items-center justify-center'}
              aria-label="Search"
            >
              <Search className={compact ? "h-3.5 w-3.5" : "w-4 h-4"} />
            </button>
          </div>
          <button
            className={compact ? compactNavClass : "p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"}
            onClick={onNext ?? handleNextDocument}
            disabled={disabled || (onNext ? !!nextDisabled : (hasNoDocuments || isAtLastDocument))}
            aria-label="Next document"
            title={disabled ? disabledTitle : 'Next document'}
          >
            <ChevronRight className={compact ? "h-3.5 w-3.5" : "w-4 h-4"} />
          </button>
          <button
            className={compact ? compactNavClass : "p-1.5 h-9 text-gray-600 hover:text-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-r-xl transition-all duration-200 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"}
            onClick={onLast ?? handleLastDocument}
            disabled={disabled || (onLast ? !!lastDisabled : (hasNoDocuments || isAtLastDocument))}
            aria-label="Last document"
            title={disabled ? disabledTitle : 'Last document'}
          >
            <ChevronsRight className={compact ? "h-3.5 w-3.5" : "w-4 h-4"} />
          </button>
        </>
      )}

      {/* Modal */}
      <Dialog open={isModalOpen && !disabled} onClose={() => { setIsModalOpen(false); setSearch(""); }} className="relative z-50">
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" aria-hidden="true" />
        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Dialog.Panel className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[78vh] overflow-hidden flex flex-col border border-gray-200">
            <div className="relative flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
              <div className="flex flex-col">
                <Dialog.Title className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Search className="w-4 h-4 text-blue-600" />
                  {t("search.searchDocuments")}
                </Dialog.Title>
                <p className="text-xs text-gray-600 mt-1">
                  {t("search.keyboardInstructions")}
                </p>
              </div>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  setSearch(""); // Clear search when X button is clicked
                }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors text-xl leading-none"
                title="Close"
              >
                ×
              </button>
            </div>

            <div className="p-4 flex-1 flex flex-col overflow-hidden">
              {sourceTypeOptions && sourceTypeOptions.length > 1 && (
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {t("packingList.generateFromType", "Generate from type")}
                  </label>
                  <select
                    value={sourceTypeId}
                    onChange={(event) => onSourceTypeChange?.(event.target.value)}
                    className="w-full h-10 rounded-lg border border-gray-300 bg-white px-3 text-sm focus:bg-blue-50 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    {sourceTypeOptions.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.code ? `${type.code} - ${type.name}` : type.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center">
                {setSearchField && <div className="inline-flex h-9 shrink-0 rounded-md border border-gray-300 bg-white p-0.5">
                  <button
                    type="button"
                    onClick={() => setSearchField?.("code")}
                    className={`px-3 text-xs font-semibold rounded transition-colors ${
                      searchField === "code"
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {t("tableHeaders.number") || "Code"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setSearchField?.("name")}
                    className={`px-3 text-xs font-semibold rounded transition-colors ${
                      searchField === "name"
                        ? "bg-blue-600 text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {t("tableHeaders.name") || "Name"}
                  </button>
                </div>}
                <div className="relative min-w-0 flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={handleKeyDownOnInput}
                    placeholder={searchPlaceholder}
                    className="w-full pl-10 pr-4 h-10 border border-gray-300 rounded-lg bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 text-sm"
                    autoFocus
                  />
                </div>
              </div>

              <div
                ref={scrollContainerRef}
                className="flex-1 overflow-y-auto border border-gray-200 rounded-lg"
                style={{ minHeight: '220px', maxHeight: '52vh', scrollBehavior: 'auto' }}
                onScroll={handleScrollLoadMore}
              >
                {loading && docs.length === 0 ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Searching...</span>
                  </div>
                ) : (
                  <table className="w-full border-collapse">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-left">
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.number")}</th>
                        {!isPocetnaSostojba && !showPlanName && !hidePartnerColumn && !showItemColumn && <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.partner")}</th>}
                        {showPlanName && <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.planName")}</th>}
                        {showItemColumn && <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("sidebar.items")}</th>}
                        {/* personInCharge column removed per NATASHA.md */}
                        {showLineCount && <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("packingList.lineCount", "Items")}</th>}
                        {!hideStatusColumn && <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("sidebar.status")}</th>}
                        <th className="py-3 px-4 font-semibold text-gray-700 border-b">{t("tableHeaders.dateTo")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {docs.map((doc, idx) => {
                        const isActive = doc.number === currentDocumentNumber;
                        const isHighlighted = idx === highlightedIndex;
                        const docNote = getDocNote?.(doc) ?? null;
                        return (
                        <tr
                          key={doc._id}
                          data-row-index={idx}
                          className={`border-b border-gray-100 cursor-pointer transition-all duration-200 ${
                            isHighlighted
                              ? (compact
                                ? "bg-blue-50 border-blue-300 ring-2 ring-blue-500/50 shadow-sm"
                                : "bg-yellow-100 border-yellow-300 ring-2 ring-yellow-400 ring-opacity-50 shadow-md")
                              : docNote
                              ? "bg-gray-50 text-gray-400"
                              : isActive
                              ? "bg-blue-50 border-l-4 border-l-blue-500"
                              : "hover:bg-gray-50"
                          }`}
                          onMouseEnter={() => setHighlightedIndex(idx)}
                          onClick={() => {
                            onSelect?.(doc);
                            setIsModalOpen(false);
                            setSearch("");
                          }}
                        >
                          <td className={`py-3 px-4 font-medium transition-colors duration-200 ${
                            isHighlighted ? (compact ? "text-blue-900" : "text-yellow-900") : isActive ? "text-blue-800" : "text-gray-900"
                          }`}>
                            {doc.number}
                            {docNote && <div className="text-xs font-normal text-gray-500">{docNote}</div>}
                          </td>
                          {!isPocetnaSostojba && !showPlanName && !hidePartnerColumn && !showItemColumn && <td className={`py-3 px-4 transition-colors duration-200 ${
                            isHighlighted ? (compact ? "text-blue-800" : "text-yellow-800") : isActive ? "text-blue-700" : "text-gray-700"
                          }`}>{(doc.supplier as any)?.name ?? (doc.partner as any)?.name ?? (doc as any).primaryBuyer?.name ?? (doc as any).buyer?.name ?? "-"}</td>}
                          {showPlanName && <td className={`py-3 px-4 transition-colors duration-200 ${
                            isHighlighted ? (compact ? "text-blue-800" : "text-yellow-800") : isActive ? "text-blue-700" : "text-gray-700"
                          }`}>{(doc as any).planName ?? "-"}</td>}
                          {showItemColumn && <td className={`py-3 px-4 transition-colors duration-200 ${
                            isHighlighted ? (compact ? "text-blue-800" : "text-yellow-800") : isActive ? "text-blue-700" : "text-gray-700"
                          }`}>{(doc as any).item?.ident ?? "-"}</td>}
                          {/* personInCharge cell removed per NATASHA.md */}
                          {showLineCount && <td className="py-3 px-4 text-sm text-gray-600">{(doc as any).lineCount ?? "-"}</td>}
                          {!hideStatusColumn && <td className="py-3 px-4">
                            <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full transition-colors duration-200 ${
                              isHighlighted
                                ? (compact
                                  ? "bg-blue-100 text-blue-900 border border-blue-300"
                                  : "bg-yellow-200 text-yellow-900 border border-yellow-400")
                                : isActive
                                ? "bg-blue-100 text-blue-800 border border-blue-300"
                                : "bg-green-100 text-green-800"
                            }`}>
                              {doc.status?.name ?? "Active"}
                            </span>
                          </td>}
                          <td className={`py-3 px-4 text-sm transition-colors duration-200 ${
                            isHighlighted ? (compact ? "text-blue-800" : "text-yellow-800") : isActive ? "text-blue-600" : "text-gray-600"
                          }`}>
                            {doc.date ? new Date(doc.date).toLocaleDateString("en-GB").replace(/\//g, ".") : "-"}
                          </td>
                        </tr>
                        );
                      })}
                      {docs.length === 0 && !loading && (
                        <tr>
                          <td colSpan={isPocetnaSostojba ? 4 : 6} className="text-center text-gray-500 py-12">
                            <div className="flex flex-col items-center gap-2">
                              <Search className="w-8 h-8 text-gray-300" />
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                )}
                
                {/* Infinite scroll loading indicator */}
                {loading && page > 1 && (
                  <div className="flex items-center justify-center py-3 border-t border-gray-100">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                    <span className="ml-2 text-xs text-gray-600">Loading more...</span>
                  </div>
                )}
                
                {/* Invisible trigger for infinite scroll */}
                {page < totalPages && (
                  <div 
                    ref={loadingRef} 
                    className="h-1 w-full"
                    style={{ opacity: 0 }}
                  />
                )}
              </div>
              
            </div>
          </Dialog.Panel>
        </div>
      </Dialog>
    </div>
  );
};

export default DocumentsMaterialSearch;
